/** @jsxImportSource @emotion/react */

import React, { ChangeEvent, useEffect, useState, useRef } from 'react';
import { useVisible } from 'react-hooks-visible';

import { QASet } from '../Data/index';
import { AnswerCard } from '../AnswerCard/answer-card';
import { QuestionHeading } from '../QuestionHeading/question-heading';
import { JingleBell } from './bell';

type questionProps = {
  index: number;
  isVisible: boolean;
  onInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onVisible: (element: HTMLFieldSetElement) => void;
  qaSet: QASet;
};

const shuffle = <T extends unknown>(_array: T[]): T[] => {
  const array = JSON.parse(JSON.stringify(_array));

  return array.sort(() => Math.random() - 0.5);
};

export const Fieldset = ({ index, onInputChange, onVisible, qaSet }: questionProps) => {
  const [targetRef, isVisible] = useVisible((vi: number) => vi > 0.5);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);

  // Copy the set of answers to prevent shuffling the shared data object.
  const answers = useRef(shuffle(qaSet.answers));

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const currentSelection = event.target.value;

    setSelectedValue(currentSelection);
    onInputChange(event);
  };

  const handleReset = () => {
    setSelectedValue(null);
  };

  useEffect(() => {
    if (isVisible && targetRef.current instanceof HTMLFieldSetElement) {
      onVisible(targetRef.current);
    }
  }, [isVisible, onVisible, targetRef]);

  useEffect(() => {
    if (!targetRef.current || !(targetRef.current instanceof HTMLFieldSetElement)) {
      return;
    }

    const form = targetRef.current.form;

    if (!form) {
      return;
    }

    form.addEventListener('reset', handleReset);

    return () => {
      form.removeEventListener('reset', handleReset);
    };
  }, [targetRef]);

  return (
    <fieldset
      css={{
        // Reset fieldset styles.
        // Moved flex to inner div due issues where alignment wasn't working on
        // a `<fieldset>`.
        border: 'none',
        margin: 0,
        padding: 0
      }}
      ref={targetRef as any}
    >
      <div
        css={{
          alignItems: 'center',
          border: 'none',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          label: 'fieldset-inner',
          paddingBottom: 150,

          [`@media (min-width: 960px)`]: {
            minHeight: 'calc(100vh - 150px)'
          }
        }}
      >
        <QuestionHeading question={qaSet.question} />

        {index === 0 && <JingleBell />}

        <div
          css={{
            alignItems: 'start',
            columnGap: '1rem',
            display: 'grid',
            gridAutoFlow: 'dense',
            gridTemplateColumns: `repeat(2, minmax(9.5rem, 1fr))`,
            label: 'card-grid',
            position: 'relative',
            rowGap: '1rem',
            width: '100%',

            [`@media (min-width: 768px)`]: {
              columnGap: '1.5rem',
              gridTemplateColumns: `repeat(auto-fit, minmax(322px, 1fr))`,
              rowGap: '1.5rem'
            }
          }}
        >
          {answers.current.map((answer, i) => {
            return (
              <AnswerCard
                key={`answercard-${answer.image.src}-key`}
                answer={answer}
                index={i}
                question={qaSet.question}
                questionIndex={index}
                questionWeight={qaSet.weight}
                handleChange={handleChange}
                selectedValue={selectedValue}
              />
            );
          })}
        </div>
      </div>
    </fieldset>
  );
};

Fieldset.displayName = 'Fieldset';
